import React, { useEffect, useCallback, useState, useMemo } from 'react';
import './CardDetail.css';

// Layouts that have two physical card faces (front and back images)
const TWO_FACED_LAYOUTS = [
  'transform',
  'modal_dfc',
  'double_faced_token',
  'reversible_card',
  'art_series'
];

// Layout where the card rotates 180 degrees (same image, rotated)
const FLIP_LAYOUT = 'flip';

// Layout where two card texts are on one face (no flip/transform)
const SPLIT_LAYOUT = 'split';

// Format name abbreviations for cleaner display
const FORMAT_ABBREVIATIONS = {
  paupercommander: 'PDH',
  standardbrawl: 'Std Brawl',
  commander: 'EDH',
  oathbreaker: 'Oath',
  oldschool: 'OS',
  premodern: 'Pre-Mod',
};

/**
 * Group printings by set+collector_number, collecting all languages for each
 */
function groupPrintingsBySetAndNumber(printings) {
  const grouped = new Map();
  
  for (const printing of printings) {
    const key = `${printing.set}-${printing.collector_number}`;
    
    if (!grouped.has(key)) {
      grouped.set(key, {
        set: printing.set,
        set_name: printing.set_name,
        collector_number: printing.collector_number,
        released_at: printing.released_at,
        rarity: printing.rarity,
        languages: [],
        printingsByLang: {}
      });
    }
    
    const group = grouped.get(key);
    if (!group.languages.includes(printing.lang)) {
      group.languages.push(printing.lang);
    }
    group.printingsByLang[printing.lang] = printing;
  }
  
  // Sort groups by release date (newest first), then sort languages within each group
  const result = Array.from(grouped.values());
  result.sort((a, b) => {
    const dateA = new Date(a.released_at || '1993-01-01');
    const dateB = new Date(b.released_at || '1993-01-01');
    return dateB - dateA;
  });
  
  // Sort languages: English first, then alphabetically
  result.forEach(group => {
    group.languages.sort((a, b) => {
      if (a === 'en') return -1;
      if (b === 'en') return 1;
      return a.localeCompare(b);
    });
  });
  
  return result;
}

/**
 * Find the best language for a group based on preferred language
 * Returns the language code to use
 */
function getBestLanguageForGroup(group, preferredLanguage) {
  // Try preferred language first
  if (group.printingsByLang[preferredLanguage]) {
    return preferredLanguage;
  }
  
  // Fall back to English if different from preferred
  if (preferredLanguage !== 'en' && group.printingsByLang['en']) {
    return 'en';
  }
  
  // Return first available language
  return group.languages[0];
}

/**
 * Mana Symbol Component - renders SVG mana symbols
 */
function ManaSymbol({ symbol, apiUrl, className = "mana-symbol" }) {
  // Convert symbol to filename: {W} -> W, {U/B} -> U-B, {C/P} -> C-P
  const filename = symbol
    .replace(/[{}]/g, '')
    .replace(/\//g, '-');
  
  const svgUrl = `${apiUrl}/images/mtg/base-assets/Resource-symbols/${filename}.svg`;
  
  return (
    <img 
      src={svgUrl} 
      alt={symbol}
      className={className}
      onError={(e) => {
        // Fallback to text if SVG not found
        e.target.style.display = 'none';
        if (e.target.nextSibling) {
          e.target.nextSibling.style.display = 'inline';
        }
      }}
    />
  );
}

/**
 * Parse and render mana cost with SVG symbols
 */
function ManaSymbols({ manaCost, apiUrl }) {
  if (!manaCost) return null;
  
  // Match all {X} patterns
  const symbolPattern = /\{[^}]+\}/g;
  const symbols = manaCost.match(symbolPattern) || [];
  
  return (
    <span className="mana-symbols">
      {symbols.map((symbol, i) => (
        <span key={i} className="mana-symbol-wrapper">
          <ManaSymbol symbol={symbol} apiUrl={apiUrl} />
          <span className="mana-symbol-fallback" style={{ display: 'none' }}>{symbol}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * Color pip with SVG symbol
 */
function ColorPip({ color, apiUrl }) {
  const svgUrl = `${apiUrl}/images/mtg/base-assets/Resource-symbols/${color}.svg`;
  
  return (
    <span className={`color-pip-svg color-${color.toLowerCase()}`}>
      <img 
        src={svgUrl} 
        alt={color}
        className="color-pip-img"
        onError={(e) => {
          e.target.style.display = 'none';
          if (e.target.nextSibling) {
            e.target.nextSibling.style.display = 'inline';
          }
        }}
      />
      <span className="color-pip-fallback" style={{ display: 'none' }}>{color}</span>
    </span>
  );
}

/**
 * Parse oracle text and render mana symbols inline
 */
function OracleText({ text, apiUrl }) {
  if (!text) return null;
  
  // Split text into parts: regular text and mana symbols
  const parts = [];
  let lastIndex = 0;
  const symbolPattern = /\{[^}]+\}/g;
  let match;
  
  while ((match = symbolPattern.exec(text)) !== null) {
    // Add text before the symbol
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    // Add the symbol
    parts.push({ type: 'symbol', content: match[0] });
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }
  
  return (
    <>
      {parts.map((part, i) => 
        part.type === 'symbol' ? (
          <span key={i} className="oracle-symbol-wrapper">
            <ManaSymbol symbol={part.content} apiUrl={apiUrl} className="oracle-mana-symbol" />
            <span className="oracle-symbol-fallback" style={{ display: 'none' }}>{part.content}</span>
          </span>
        ) : (
          <span key={i}>{part.content}</span>
        )
      )}
    </>
  );
}

/**
 * Gallery Popup Component - shows all printings in a visual grid
 */
function GalleryPopup({ printings, selectedLanguage, onLanguageChange, onSelect, onClose, apiUrl }) {
  // Get all unique languages across all printings
  const allLanguages = useMemo(() => {
    const langs = new Set();
    printings.forEach(group => {
      group.languages.forEach(l => langs.add(l));
    });
    const result = Array.from(langs);
    result.sort((a, b) => {
      if (a === 'en') return -1;
      if (b === 'en') return 1;
      return a.localeCompare(b);
    });
    return result;
  }, [printings]);

  // Filter printings that have the selected language
  const filteredPrintings = useMemo(() => {
    return printings.filter(group => group.printingsByLang[selectedLanguage]);
  }, [printings, selectedLanguage]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="gallery-overlay" onClick={handleBackdropClick}>
      <div className="gallery-modal">
        <button className="gallery-close-btn" onClick={onClose} aria-label="Close Gallery">
          ×
        </button>
        
        <div className="gallery-header">
          <h2 className="gallery-title">All Printings</h2>
          <div className="gallery-language-selector">
            {allLanguages.map(lang => (
              <button
                key={lang}
                className={`gallery-lang-btn ${selectedLanguage === lang ? 'active' : ''}`}
                onClick={() => onLanguageChange(lang)}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="gallery-grid">
          {filteredPrintings.map(group => {
            const printing = group.printingsByLang[selectedLanguage];
            const imageUrl = `${apiUrl}/images/mtg/${selectedLanguage}/${printing.id}.jpg`;
            
            return (
              <div 
                key={`${group.set}-${group.collector_number}`}
                className="gallery-item"
                onClick={() => onSelect(group)}
              >
                <div className="gallery-image-container">
                  <img 
                    src={imageUrl} 
                    alt={`${group.set_name} #${group.collector_number}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="gallery-image-placeholder" style={{ display: 'none' }}>
                    <span>◆</span>
                  </div>
                </div>
                <div className="gallery-item-info">
                  <span className="gallery-set">{group.set?.toUpperCase()}</span>
                  <span className="gallery-number">#{group.collector_number}</span>
                </div>
              </div>
            );
          })}
        </div>

        {filteredPrintings.length === 0 && (
          <div className="gallery-empty">
            No printings available in {selectedLanguage.toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
}

function CardDetail({ card: initialCard, onClose, apiUrl, searchLanguage = 'en', onArtistClick, onSetClick }) {
  const printings = initialCard.printings || [];
  
  // Group printings by set+collector_number
  const groupedPrintings = useMemo(() => 
    groupPrintingsBySetAndNumber(printings),
    [printings]
  );
  
  // Popup display language - starts with search language, can be changed by user
  // This is internal to the popup and resets when popup closes
  const [popupPreferredLanguage, setPopupPreferredLanguage] = useState(searchLanguage);
  
  // Track selected group (set+collector_number combination)
  const [selectedGroupKey, setSelectedGroupKey] = useState(() => {
    if (groupedPrintings.length === 0) return null;
    // Find a group that has the preferred language, or English, or first available
    for (const group of groupedPrintings) {
      if (group.printingsByLang[searchLanguage]) {
        return `${group.set}-${group.collector_number}`;
      }
    }
    for (const group of groupedPrintings) {
      if (group.printingsByLang['en']) {
        return `${group.set}-${group.collector_number}`;
      }
    }
    return `${groupedPrintings[0].set}-${groupedPrintings[0].collector_number}`;
  });
  
  // Track the actual language being displayed (may differ from preferred if not available)
  const [displayLanguage, setDisplayLanguage] = useState(() => {
    if (groupedPrintings.length === 0) return searchLanguage;
    const initialGroup = groupedPrintings.find(g => 
      `${g.set}-${g.collector_number}` === selectedGroupKey
    ) || groupedPrintings[0];
    return getBestLanguageForGroup(initialGroup, searchLanguage);
  });
  
  const [imageError, setImageError] = useState(false);
  const [backImageError, setBackImageError] = useState(false);
  const [showBack, setShowBack] = useState(false);
  const [showAllPrintings, setShowAllPrintings] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryLanguage, setGalleryLanguage] = useState(searchLanguage);
  // Text display mode: 'oracle' or 'printed'
  const [textMode, setTextMode] = useState('oracle');

  // Get the currently selected group
  const selectedGroup = useMemo(() => 
    groupedPrintings.find(g => `${g.set}-${g.collector_number}` === selectedGroupKey),
    [groupedPrintings, selectedGroupKey]
  );
  
  // Get the current printing based on selected group and display language
  const currentPrinting = useMemo(() => {
    if (!selectedGroup) {
      // No groups - use card directly
      const printing = printings.find(p => p.lang === displayLanguage) || printings[0];
      return printing || initialCard;
    }
    return selectedGroup.printingsByLang[displayLanguage] || 
           selectedGroup.printingsByLang[selectedGroup.languages[0]];
  }, [selectedGroup, displayLanguage, printings, initialCard]);
  
  // Get languages available for current group
  const languagesForCurrentGroup = useMemo(() => 
    selectedGroup?.languages || [displayLanguage],
    [selectedGroup, displayLanguage]
  );
  
  // Merge current printing data with base card data
  const card = useMemo(() => ({
    ...initialCard,
    id: currentPrinting.id,
    set: currentPrinting.set,
    set_name: currentPrinting.set_name,
    collector_number: currentPrinting.collector_number,
    foil: currentPrinting.foil,
    released_at: currentPrinting.released_at,
    lang: currentPrinting.lang,
    rarity: currentPrinting.rarity,
    tcgplayer_link: currentPrinting.tcgplayer_link,
    // Artist can vary by printing
    artist: currentPrinting.artist || initialCard.artist,
    // Printed text fields (localized)
    printed_name: currentPrinting.printed_name,
    printed_text: currentPrinting.printed_text,
    printed_type_line: currentPrinting.printed_type_line,
    // Flavor text should come from the current printing's language
    flavor_text: currentPrinting.flavor_text !== undefined ? currentPrinting.flavor_text : initialCard.flavor_text,
    // Include card_faces for DFCs
    card_faces: currentPrinting.card_faces || initialCard.card_faces,
  }), [initialCard, currentPrinting]);

  // Determine the card's layout type for display purposes
  const isTwoFaced = TWO_FACED_LAYOUTS.includes(card.layout);
  const isFlipCard = card.layout === FLIP_LAYOUT;
  const isSplitCard = card.layout === SPLIT_LAYOUT;
  const hasCardFaces = card.card_faces && card.card_faces.length > 0;

  // Get sorted card faces (front first)
  const sortedFaces = useMemo(() => {
    if (!hasCardFaces) return [];
    return [...card.card_faces].sort((a, b) => {
      if (a.side === 'front') return -1;
      if (b.side === 'front') return 1;
      return 0;
    });
  }, [card.card_faces, hasCardFaces]);

  // For two-faced cards, get the current face based on showBack state
  const currentFace = useMemo(() => {
    if (!isTwoFaced || sortedFaces.length === 0) {
      return null;
    }
    return showBack && sortedFaces.length > 1 ? sortedFaces[1] : sortedFaces[0];
  }, [isTwoFaced, sortedFaces, showBack]);

  // For flip cards (rotate 180), get front face data but don't switch faces
  const flipCardFace = useMemo(() => {
    if (!isFlipCard || sortedFaces.length === 0) {
      return null;
    }
    // For flip cards, showBack means we're showing the "flipped" text (second face)
    return showBack && sortedFaces.length > 1 ? sortedFaces[1] : sortedFaces[0];
  }, [isFlipCard, sortedFaces, showBack]);

  // Helper to get the appropriate text value based on text mode (oracle vs printed)
  const getTextValue = (oracleValue, printedValue) => {
    if (textMode === 'printed' && printedValue) {
      return printedValue;
    }
    return oracleValue;
  };

  // Check if printed text is available
  const hasPrintedText = useMemo(() => {
    if (isTwoFaced && currentFace) {
      return !!(currentFace.printed_name || currentFace.printed_text || currentFace.printed_type_line);
    }
    if (isFlipCard && flipCardFace) {
      return !!(flipCardFace.printed_name || flipCardFace.printed_text || flipCardFace.printed_type_line);
    }
    if (isSplitCard && sortedFaces.length > 0) {
      return sortedFaces.some(f => f.printed_name || f.printed_text || f.printed_type_line);
    }
    return !!(card.printed_name || card.printed_text || card.printed_type_line);
  }, [isTwoFaced, isFlipCard, isSplitCard, currentFace, flipCardFace, sortedFaces, card]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      if (showGallery) {
        setShowGallery(false);
      } else {
        onClose();
      }
    }
  }, [onClose, showGallery]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  // Reset image error when printing changes
  useEffect(() => {
    setImageError(false);
    setBackImageError(false);
    setShowBack(false);
  }, [currentPrinting.id]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Language-based image path
  const lang = card.lang || 'en';
  const frontImageUrl = `${apiUrl}/images/mtg/${lang}/${card.id}.jpg`;
  const backImageUrl = `${apiUrl}/images/mtg/${lang}/${card.id}_back.jpg`;
  const notFoundBackUrl = `${apiUrl}/images/mtg/${lang}/not-found_back.jpg`;

  // Check if this card supports flipping (two-faced or flip layout)
  const canFlip = isTwoFaced || isFlipCard;

  // Get current image URL based on flip state and card type
  const getCurrentImageUrl = () => {
    if (!showBack) {
      return frontImageUrl;
    }
    // For flip cards, we show the same image (rotation handled in CSS)
    if (isFlipCard) {
      return frontImageUrl;
    }
    // For two-faced cards, show the back image
    return backImageError ? notFoundBackUrl : backImageUrl;
  };

  // Get flip button label based on card type
  const getFlipButtonLabel = () => {
    if (isFlipCard) {
      return showBack ? 'Flip Back' : 'Flip Card';
    }
    return showBack ? 'Show Front' : 'Show Back';
  };

  const handleFlip = () => {
    setShowBack(!showBack);
  };

  const handleImageError = () => {
    if (showBack && !isFlipCard) {
      setBackImageError(true);
    } else {
      setImageError(true);
    }
  };

  const handleGroupClick = (group) => {
    const newKey = `${group.set}-${group.collector_number}`;
    setSelectedGroupKey(newKey);
    
    // When selecting a new group, find the best language based on popup preference
    const bestLang = getBestLanguageForGroup(group, popupPreferredLanguage);
    setDisplayLanguage(bestLang);
  };

  // Handle manual language selection by user
  const handleLanguageSelect = (lang) => {
    setDisplayLanguage(lang);
    // Update the popup's preferred language for subsequent group selections
    // This does NOT affect the search results or parent component
    setPopupPreferredLanguage(lang);
  };

  const handleGallerySelect = (group) => {
    handleGroupClick(group);
    setShowGallery(false);
  };

  const openGallery = () => {
    setGalleryLanguage(displayLanguage);
    setShowGallery(true);
  };

  const getRarityLabel = (rarity) => {
    if (!rarity) return 'Unknown';
    return rarity.charAt(0).toUpperCase() + rarity.slice(1);
  };

  // Get display name for format (abbreviated if needed)
  const getFormatDisplayName = (format) => {
    if (FORMAT_ABBREVIATIONS[format]) {
      return FORMAT_ABBREVIATIONS[format];
    }
    return format.charAt(0).toUpperCase() + format.slice(1);
  };

  // Get abbreviated status
  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'not_legal':
        return 'Not Legal';
      case 'legal':
        return 'Legal';
      case 'banned':
        return 'Banned';
      case 'restricted':
        return 'Restricted';
      default:
        return status.replace('_', ' ');
    }
  };

  // Get language display text for printings table
  const getLanguageDisplay = (languages) => {
    if (languages.length === 1) {
      return languages[0].toUpperCase();
    }
    return 'Multi';
  };

  // Get printings to display (limited or all)
  const displayedPrintings = showAllPrintings ? groupedPrintings : groupedPrintings.slice(0, 5);
  const hasMorePrintings = groupedPrintings.length > 5;

  return (
    <div className="card-detail-overlay" onClick={handleBackdropClick}>
      <div className="card-detail-modal">
        <button className="close-btn" onClick={onClose} aria-label="Close">
          ×
        </button>
        
        <div className="card-detail-content">
          {/* Card Image Column */}
          <div className="card-detail-image-column">
            <div className={`card-detail-image ${isFlipCard && showBack ? 'flip-rotated' : ''}`}>
              {!imageError || (showBack && !isFlipCard) ? (
                <img
                  src={getCurrentImageUrl()}
                  alt={showBack ? `${card.name} (Back)` : card.name}
                  onError={handleImageError}
                  key={`${card.id}-${showBack ? 'back' : 'front'}`}
                />
              ) : (
                <div className="image-placeholder">
                  <span className="placeholder-symbol">◆</span>
                  <span className="placeholder-text">Image not available</span>
                </div>
              )}
            </div>
            
            {/* Flip Card Button */}
            {canFlip && (
              <button className="flip-card-btn" onClick={handleFlip}>
                <span className="flip-icon">⟳</span>
                {getFlipButtonLabel()}
              </button>
            )}

            {/* Printings Table - Under the card image */}
            {groupedPrintings.length > 1 && (
              <div className="card-printings">
                <h3 className="printings-title">
                  All Printings
                  <button 
                    className="printings-count-btn"
                    onClick={openGallery}
                    title="Gallery"
                  >
                    ({groupedPrintings.length})
                  </button>
                </h3>
                <div className="printings-table-wrapper">
                  <table className="printings-table">
                    <thead>
                      <tr>
                        <th>Set</th>
                        <th>#</th>
                        <th>Lang</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedPrintings.map((group, index) => {
                        const groupKey = `${group.set}-${group.collector_number}`;
                        const isSelected = groupKey === selectedGroupKey;
                        return (
                          <tr 
                            key={groupKey}
                            className={`printing-row ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleGroupClick(group)}
                            title={group.set_name}
                          >
                            <td className="printing-set">
                              {group.set?.toUpperCase()}
                            </td>
                            <td className="printing-number">{group.collector_number}</td>
                            <td className={`printing-lang ${group.languages.length > 1 ? 'multi' : ''}`}>
                              {getLanguageDisplay(group.languages)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {hasMorePrintings && (
                  <button 
                    className="show-more-btn"
                    onClick={() => setShowAllPrintings(!showAllPrintings)}
                  >
                    {showAllPrintings 
                      ? 'Show Less' 
                      : `+ ${groupedPrintings.length - 5} more`}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Card Info */}
          <div className="card-detail-info">
            {/* Card Info - Different display based on card type */}
            
            {/* SPLIT CARDS: Show name/mana from top level, two text boxes from faces */}
            {isSplitCard && sortedFaces.length >= 2 ? (
              <>
                <header className="card-header">
                  <h2 className="card-title">
                    {getTextValue(card.name, card.printed_name)}
                  </h2>
                  {card.mana_cost && (
                    <span className="card-mana-cost">
                      <ManaSymbols manaCost={card.mana_cost} apiUrl={apiUrl} />
                    </span>
                  )}
                </header>

                {/* Text Mode Toggle */}
                <div className="text-mode-toggle">
                  <button
                    className={`text-mode-btn ${textMode === 'oracle' ? 'active' : ''}`}
                    onClick={() => setTextMode('oracle')}
                  >
                    Oracle Text
                  </button>
                  <button
                    className={`text-mode-btn ${textMode === 'printed' ? 'active' : ''}`}
                    onClick={() => setTextMode('printed')}
                    disabled={!hasPrintedText}
                    title={!hasPrintedText ? 'No printed text available for this printing' : ''}
                  >
                    Printed Text
                  </button>
                </div>

                {/* Two text boxes for split cards */}
                {sortedFaces.map((face, idx) => (
                  <div key={idx} className="split-card-face">
                    <div className="split-face-header">
                      <span className="split-face-name">
                        {getTextValue(face.name, face.printed_name)}
                      </span>
                      {face.mana_cost && (
                        <span className="split-face-mana">
                          <ManaSymbols manaCost={face.mana_cost} apiUrl={apiUrl} />
                        </span>
                      )}
                    </div>
                    <div className="card-type-line">
                      {getTextValue(face.type_line, face.printed_type_line)}
                    </div>
                    {(face.oracle_text || face.printed_text) && (
                      <div className="card-oracle-text">
                        {getTextValue(face.oracle_text, face.printed_text)?.split('\n').map((line, i) => (
                          <p key={i}><OracleText text={line} apiUrl={apiUrl} /></p>
                        ))}
                      </div>
                    )}
                    {(face.power !== undefined && face.power !== null) && (
                      <div className="card-pt">
                        {face.power}/{face.toughness}
                      </div>
                    )}
                  </div>
                ))}

                {card.flavor_text && (
                  <div className="card-flavor-text">
                    <em>{card.flavor_text}</em>
                  </div>
                )}
              </>
            ) : (
              /* TWO-FACED, FLIP, or NORMAL CARDS */
              <>
                <header className="card-header">
                  <h2 className="card-title">
                    {(() => {
                      // Two-faced cards: use current face
                      if (isTwoFaced && currentFace) {
                        return getTextValue(currentFace.name, currentFace.printed_name);
                      }
                      // Flip cards: use flip card face
                      if (isFlipCard && flipCardFace) {
                        return getTextValue(flipCardFace.name, flipCardFace.printed_name);
                      }
                      // Normal cards
                      return getTextValue(card.name, card.printed_name);
                    })()}
                  </h2>
                  {(() => {
                    const manaCost = (isTwoFaced && currentFace) ? currentFace.mana_cost :
                                     (isFlipCard && flipCardFace) ? flipCardFace.mana_cost :
                                     card.mana_cost;
                    return manaCost && (
                      <span className="card-mana-cost">
                        <ManaSymbols manaCost={manaCost} apiUrl={apiUrl} />
                      </span>
                    );
                  })()}
                </header>

                <div className="card-type-line">
                  {(() => {
                    if (isTwoFaced && currentFace) {
                      return getTextValue(currentFace.type_line, currentFace.printed_type_line);
                    }
                    if (isFlipCard && flipCardFace) {
                      return getTextValue(flipCardFace.type_line, flipCardFace.printed_type_line);
                    }
                    return getTextValue(card.type_line, card.printed_type_line);
                  })()}
                </div>

                {/* Text Mode Toggle */}
                <div className="text-mode-toggle">
                  <button
                    className={`text-mode-btn ${textMode === 'oracle' ? 'active' : ''}`}
                    onClick={() => setTextMode('oracle')}
                  >
                    Oracle Text
                  </button>
                  <button
                    className={`text-mode-btn ${textMode === 'printed' ? 'active' : ''}`}
                    onClick={() => setTextMode('printed')}
                    disabled={!hasPrintedText}
                    title={!hasPrintedText ? 'No printed text available for this printing' : ''}
                  >
                    Printed Text
                  </button>
                </div>

                {(() => {
                  let oracleText, printedText;
                  if (isTwoFaced && currentFace) {
                    oracleText = currentFace.oracle_text;
                    printedText = currentFace.printed_text;
                  } else if (isFlipCard && flipCardFace) {
                    oracleText = flipCardFace.oracle_text;
                    printedText = flipCardFace.printed_text;
                  } else {
                    oracleText = card.oracle_text;
                    printedText = card.printed_text;
                  }
                  const displayText = getTextValue(oracleText, printedText);
                  return displayText && (
                    <div className="card-oracle-text">
                      {displayText.split('\n').map((line, i) => (
                        <p key={i}><OracleText text={line} apiUrl={apiUrl} /></p>
                      ))}
                    </div>
                  );
                })()}

                {(() => {
                  const flavorText = (isTwoFaced && currentFace) ? currentFace.flavor_text :
                                     (isFlipCard && flipCardFace) ? flipCardFace.flavor_text :
                                     card.flavor_text;
                  return flavorText && (
                    <div className="card-flavor-text">
                      <em>{flavorText}</em>
                    </div>
                  );
                })()}

                {(() => {
                  const power = (isTwoFaced && currentFace) ? currentFace.power :
                                (isFlipCard && flipCardFace) ? flipCardFace.power :
                                card.power;
                  const toughness = (isTwoFaced && currentFace) ? currentFace.toughness :
                                    (isFlipCard && flipCardFace) ? flipCardFace.toughness :
                                    card.toughness;
                  return (power !== undefined && power !== null) && (
                    <div className="card-pt">
                      {power}/{toughness}
                    </div>
                  );
                })()}
              </>
            )}

            <div className="card-details-grid">
              {/* Language selector buttons */}
              <div className="detail-item detail-item-full">
                <span className="detail-label">Language</span>
                <div className="language-buttons">
                  {languagesForCurrentGroup.map(langCode => (
                    <button
                      key={langCode}
                      className={`lang-btn ${displayLanguage === langCode ? 'active' : ''}`}
                      onClick={() => handleLanguageSelect(langCode)}
                    >
                      {langCode.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="detail-item">
                <span className="detail-label">Set</span>
                <span className="detail-value">
                  <button
                    className="clickable-link"
                    onClick={() => onSetClick && onSetClick(card.set_name, card.set)}
                    title={`Search for cards in ${card.set_name}`}
                  >
                    {card.set_name}
                  </button>
                  {' '}({card.set?.toUpperCase()})
                </span>
              </div>

              <div className="detail-item">
                <span className="detail-label">Rarity</span>
                <span className={`detail-value rarity-${card.rarity}`}>
                  {getRarityLabel(card.rarity)}
                </span>
              </div>

              {(() => {
                const artist = (isTwoFaced && currentFace) ? currentFace.artist :
                               (isFlipCard && flipCardFace) ? flipCardFace.artist :
                               card.artist;
                return artist && (
                  <div className="detail-item">
                    <span className="detail-label">Artist</span>
                    <span className="detail-value">
                      <button
                        className="clickable-link"
                        onClick={() => onArtistClick && onArtistClick(artist)}
                        title={`Search for cards by ${artist}`}
                      >
                        {artist}
                      </button>
                    </span>
                  </div>
                );
              })()}

              <div className="detail-item">
                <span className="detail-label">Collector #</span>
                <span className="detail-value">{card.collector_number}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">Foil</span>
                <span className={`detail-value ${card.foil ? 'foil-yes' : 'foil-no'}`}>
                  {card.foil ? 'Yes' : 'No'}
                </span>
              </div>

              {(() => {
                const colors = (isTwoFaced && currentFace?.colors?.length > 0) ? currentFace.colors :
                               (isFlipCard && flipCardFace?.colors?.length > 0) ? flipCardFace.colors :
                               card.colors;
                return (colors && colors.length > 0) && (
                  <div className="detail-item">
                    <span className="detail-label">Colors</span>
                    <span className="detail-value color-pips">
                      {colors.map(c => (
                        <ColorPip key={c} color={c} apiUrl={apiUrl} />
                      ))}
                    </span>
                  </div>
                );
              })()}

              {card.cmc !== undefined && (
                <div className="detail-item">
                  <span className="detail-label">Mana Value</span>
                  <span className="detail-value">{card.cmc}</span>
                </div>
              )}
            </div>

            {/* TCGPlayer Link */}
            {card.tcgplayer_link && (
              <div className="tcgplayer-section">
                <a 
                  href={card.tcgplayer_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="tcgplayer-link"
                >
                  View on TCGPlayer
                  <span className="external-icon">↗</span>
                </a>
              </div>
            )}

            {/* Legalities */}
            {card.legalities && (
              <div className="card-legalities">
                <h3 className="legalities-title">Format Legality</h3>
                <div className="legalities-grid">
                  {Object.entries(card.legalities).map(([format, status]) => (
                    <div key={format} className={`legality-item legality-${status.replace('_', '-')}`}>
                      <span className="legality-format" title={format}>
                        {getFormatDisplayName(format)}
                      </span>
                      <span className="legality-status">
                        {getStatusDisplayName(status)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Keywords */}
            {card.keywords && card.keywords.length > 0 && (
              <div className="card-keywords">
                <h3 className="keywords-title">Keywords</h3>
                <div className="keywords-list">
                  {card.keywords.map(keyword => (
                    <span key={keyword} className="keyword-tag">{keyword}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gallery Popup */}
      {showGallery && (
        <GalleryPopup
          printings={groupedPrintings}
          selectedLanguage={galleryLanguage}
          onLanguageChange={setGalleryLanguage}
          onSelect={handleGallerySelect}
          onClose={() => setShowGallery(false)}
          apiUrl={apiUrl}
        />
      )}
    </div>
  );
}

export default CardDetail;
